import React, { useMemo, useState } from 'react';

import { Tag } from 'antd-v5';
import cn from 'classnames';
import { MdAddCircle, MdCancel } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Search, Text } from 'scouting-ui/lib';
import MemberTypeTag from 'scouting-ui/lib/Tags/MemberTypeTag';

import type { SubUnitType } from '@appTypes/esb';
import type * as SubUnitAdmin from '@modules/roster-v2/models/subUnitAdmin';
import PersonAvatar from '@shared/components/PersonAvatar';
import { intl } from '@shared/localization';

import styles from './SubUnitModal.module.scss';

export type SubUnitMemberGroup = 'youths' | 'adults' | 'denChiefs';

/** `MemberTransfer` props, for one member list */
export interface MemberPickerProps {
  group: SubUnitMemberGroup;
  subUnitType: SubUnitType;
  /** In order; `unavailable` ones are listed only when already in */
  options: SubUnitAdmin.SubUnitMemberOption[];
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

interface MemberInfoProps {
  member: SubUnitAdmin.SubUnitMemberOption;
  /** Picked: warns that a youth stays in their other dens/patrols */
  selected?: boolean;
  className?: string;
}

const MemberInfo: React.FC<MemberInfoProps> = props => {
  const { member, selected = false, className } = props;

  const isAdult = member.kind === 'adult';
  const detail = isAdult ? member.detail : undefined;
  // Adults and den chiefs often serve several dens: only youths are warned
  const warnOtherSubUnits =
    selected && (member.kind === 'youth' || member.kind === 'participant');

  return (
    <span className={cn(styles.member, className)}>
      <PersonAvatar
        size="lg"
        isAdult={isAdult}
        gender={member.gender}
        pictureUrl={member.pictureUrl ?? undefined}
      />
      <span className={styles.memberInfo}>
        <span className={styles.memberNameLine}>
          <Text className={styles.memberName}>{member.label}</Text>
          {member.kind === 'participant' && (
            <MemberTypeTag memberType="participant" />
          )}
          {member.unavailable === 'expired' && (
            <Tag color="error" className={styles.memberTag}>
              <FormattedMessage id="rosterV2.subUnits.member.expired" />
            </Tag>
          )}
        </span>
        {(detail || member.currentSubUnits.length > 0) && (
          <span className={styles.memberDetailLine}>
            {detail && (
              <Text font="body-caption" type="secondary">
                {detail}
              </Text>
            )}
            {member.currentSubUnits.map(subUnit =>
              warnOtherSubUnits ? (
                <Tag key={subUnit} color="warning" className={styles.memberTag}>
                  <FormattedMessage
                    id="rosterV2.subUnits.member.alsoIn"
                    values={{ subUnit }}
                  />
                </Tag>
              ) : (
                <Tag key={subUnit} className={styles.memberTag}>
                  {subUnit}
                </Tag>
              ),
            )}
          </span>
        )}
      </span>
    </span>
  );
};

type Side = 'available' | 'inSubUnit';

interface TransferListProps {
  side: Side;
  group: SubUnitMemberGroup;
  subUnitType: MemberPickerProps['subUnitType'];
  members: SubUnitAdmin.SubUnitMemberOption[];
  /** Nobody of this group is on the roster at all */
  rosterEmpty: boolean;
  onMove: (id: number) => void;
  disabled?: boolean;
}

const TransferList: React.FC<TransferListProps> = props => {
  const { side, group, subUnitType, members, rosterEmpty, onMove, disabled } =
    props;

  const [search, setSearch] = useState('');

  const query = search.trim().toLowerCase();
  const shown = query
    ? members.filter(member => member.label.toLowerCase().includes(query))
    : members;

  const isAvailable = side === 'available';
  const titleId = isAvailable
    ? `rosterV2.subUnits.picker.transfer.title.available.${group}`
    : `rosterV2.subUnits.picker.transfer.title.inSubUnit.${group}.${subUnitType}`;
  let emptyId = `rosterV2.subUnits.picker.transfer.empty.${side}.${subUnitType}`;
  if (query) emptyId = 'rosterV2.subUnits.picker.transfer.noMatches';
  else if (rosterEmpty && isAvailable) {
    emptyId = `rosterV2.subUnits.picker.transfer.empty.none.${group}`;
  }
  const searchPlaceholder = intl.formatMessage({
    id: `rosterV2.subUnits.picker.search.${group}`,
  });

  return (
    <section className={styles.pickerList}>
      <Text strong className={styles.pickerTitle}>
        <FormattedMessage id={titleId} values={{ count: members.length }} />
      </Text>
      <Search
        size="small"
        allowClear
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        value={search}
        onChange={e => setSearch(e.target.value)}
        // The list filters as you type; Enter mustn't submit the form around it
        onPressEnter={e => e.preventDefault()}
        disabled={disabled}
      />
      {shown.length > 0 ? (
        <ul className={styles.pickerRows}>
          {shown.map(member => (
            <li key={member.value}>
              <button
                type="button"
                className={cn(styles.pickerRow, {
                  [styles.pickerRowInSubUnit]: !isAvailable,
                })}
                disabled={disabled}
                aria-label={intl.formatMessage(
                  {
                    id: isAvailable
                      ? 'rosterV2.subUnits.picker.transfer.add'
                      : 'rosterV2.subUnits.picker.transfer.remove',
                  },
                  { name: member.label },
                )}
                onClick={() => onMove(member.value)}
              >
                <MemberInfo member={member} selected={!isAvailable} />
                {isAvailable ? (
                  <MdAddCircle
                    className={cn(styles.pickerIcon, styles.pickerAddIcon)}
                  />
                ) : (
                  <MdCancel
                    className={cn(styles.pickerIcon, styles.pickerRemoveIcon)}
                  />
                )}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <Text
          font="body-caption"
          type="secondary"
          className={styles.pickerEmpty}
        >
          <FormattedMessage id={emptyId} />
        </Text>
      )}
    </section>
  );
};

const MemberTransfer: React.FC<MemberPickerProps> = props => {
  const { group, subUnitType, options, value, onChange, disabled } = props;

  const selectedIds = useMemo(() => new Set(value), [value]);
  // Both lists keep roster order, so a moved person lands predictably.
  // People who can't be added only show when already in, to be removed
  const available = useMemo(
    () =>
      options.filter(
        member => !selectedIds.has(member.value) && !member.unavailable,
      ),
    [options, selectedIds],
  );
  const inSubUnit = useMemo(
    () => options.filter(member => selectedIds.has(member.value)),
    [options, selectedIds],
  );

  const handleMove = (id: number) =>
    onChange(
      selectedIds.has(id) ? value.filter(v => v !== id) : [...value, id],
    );

  const listProps = {
    group,
    subUnitType,
    rosterEmpty: !options.some(member => !member.unavailable),
    onMove: handleMove,
    disabled,
  };

  return (
    <div className={styles.picker}>
      <div className={styles.pickerLists}>
        <TransferList {...listProps} side="available" members={available} />
        <TransferList {...listProps} side="inSubUnit" members={inSubUnit} />
      </div>
      <Text font="body-caption" type="secondary">
        <FormattedMessage id="rosterV2.subUnits.picker.transfer.hint" />
      </Text>
    </div>
  );
};

export default MemberTransfer;
